import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { BRAND } from "../lib/brand";
import { useNight } from "../lib/worldTheme";
import { scatterPositions, worldObstacles, getBenches } from "./helpers";
import { FloraCluster, FLOWER_COLORS } from "./Flora";

function Tree({ position, scale = 1 }) {
  const night = useNight();
  const foliage = night ? "#3E5A38" : "#5F8B49";
  const foliage2 = night ? "#34502F" : "#6E9B57";
  return (
    <group position={position} scale={scale}>
      <mesh position={[0, 0.6, 0]} castShadow><cylinderGeometry args={[0.16, 0.22, 1.2, 7]} /><meshStandardMaterial color={"#6B5640"} roughness={0.9} /></mesh>
      <mesh position={[0, 1.5, 0]} castShadow><icosahedronGeometry args={[0.95, 0]} /><meshStandardMaterial color={foliage} roughness={1} flatShading /></mesh>
      <mesh position={[0.4, 2.1, 0.2]} castShadow><icosahedronGeometry args={[0.6, 0]} /><meshStandardMaterial color={foliage2} roughness={1} flatShading /></mesh>
      <mesh position={[-0.35, 2.0, -0.2]} castShadow><icosahedronGeometry args={[0.5, 0]} /><meshStandardMaterial color={foliage2} roughness={1} flatShading /></mesh>
    </group>
  );
}

function Bush({ position, scale = 1 }) {
  const night = useNight();
  const c = night ? "#2C4A33" : "#5E8A46";
  return (
    <group position={position} scale={scale}>
      <mesh castShadow position={[0, 0.32, 0]}><icosahedronGeometry args={[0.5, 0]} /><meshStandardMaterial color={c} roughness={1} flatShading /></mesh>
      <mesh castShadow position={[0.36, 0.22, 0.12]}><icosahedronGeometry args={[0.34, 0]} /><meshStandardMaterial color={c} roughness={1} flatShading /></mesh>
      <mesh castShadow position={[-0.3, 0.2, -0.1]}><icosahedronGeometry args={[0.3, 0]} /><meshStandardMaterial color={c} roughness={1} flatShading /></mesh>
    </group>
  );
}

function Rock({ position, scale = 1 }) {
  const night = useNight();
  return (
    <group position={position} scale={scale}>
      <mesh castShadow position={[0, 0.18, 0]} rotation={[0.3, 0.6, 0.2]}><dodecahedronGeometry args={[0.42, 0]} /><meshStandardMaterial color={night ? "#3A3F49" : "#9A968C"} roughness={1} flatShading /></mesh>
    </group>
  );
}

function Lamp({ position }) {
  const night = useNight();
  return (
    <group position={position}>
      <mesh position={[0, 1.2, 0]} castShadow><cylinderGeometry args={[0.07, 0.1, 2.4, 6]} /><meshStandardMaterial color={BRAND.charcoal} roughness={0.6} /></mesh>
      <mesh position={[0, 2.45, 0]}><sphereGeometry args={[0.2, 12, 12]} /><meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={night ? 2.2 : 0.6} /></mesh>
      {night && (<mesh position={[0, 2.45, 0]}><sphereGeometry args={[0.5, 10, 10]} /><meshBasicMaterial color={BRAND.yellow} transparent opacity={0.16} depthWrite={false} /></mesh>)}
    </group>
  );
}

function Bench({ position, rotation = 0 }) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, 0.3, 0]} castShadow><boxGeometry args={[1.4, 0.1, 0.45]} /><meshStandardMaterial color={BRAND.charcoal} roughness={0.7} /></mesh>
      <mesh position={[0, 0.55, -0.18]} castShadow><boxGeometry args={[1.4, 0.4, 0.08]} /><meshStandardMaterial color={BRAND.charcoal} roughness={0.7} /></mesh>
      <mesh position={[-0.6, 0.15, 0]}><boxGeometry args={[0.08, 0.3, 0.4]} /><meshStandardMaterial color={"#54585F"} /></mesh>
      <mesh position={[0.6, 0.15, 0]}><boxGeometry args={[0.08, 0.3, 0.4]} /><meshStandardMaterial color={"#54585F"} /></mesh>
    </group>
  );
}

function FlowerBed({ position }) {
  const night = useNight();
  const dots = useMemo(() => new Array(9).fill(0).map((_, i) => ({ x: ((i % 3) - 1) * 0.4, z: (Math.floor(i / 3) - 1) * 0.4, c: FLOWER_COLORS[i % FLOWER_COLORS.length] })), []);
  return (
    <group position={position}>
      <mesh position={[0, 0.08, 0]}><cylinderGeometry args={[1.0, 1.0, 0.16, 12]} /><meshStandardMaterial color={night ? "#22402E" : "#557C3F"} roughness={1} /></mesh>
      {dots.map((dt, i) => (<mesh key={i} position={[dt.x, 0.26, dt.z]}><sphereGeometry args={[0.12, 8, 8]} /><meshStandardMaterial color={dt.c} emissive={dt.c} emissiveIntensity={night ? 0.5 : 0.1} /></mesh>))}
    </group>
  );
}

function Fountain({ position }) {
  const night = useNight();
  const w1 = useRef();
  const w2 = useRef();
  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (w1.current) w1.current.scale.y = 1 + Math.sin(t * 3) * 0.12;
    if (w2.current) w2.current.scale.y = 1 + Math.sin(t * 3 + 1) * 0.12;
  });
  return (
    <group position={position}>
      <mesh position={[0, 0.25, 0]} castShadow receiveShadow><cylinderGeometry args={[2.2, 2.4, 0.5, 24]} /><meshStandardMaterial color={night ? "#2A2E3A" : "#CFCABB"} roughness={1} /></mesh>
      <mesh position={[0, 0.45, 0]}><cylinderGeometry args={[1.9, 1.9, 0.18, 24]} /><meshStandardMaterial color={"#7FB9CE"} transparent opacity={0.8} roughness={0.2} metalness={0.1} emissive={"#7FB9CE"} emissiveIntensity={night ? 0.3 : 0.05} /></mesh>
      <mesh position={[0, 0.8, 0]} castShadow><cylinderGeometry args={[0.6, 0.8, 0.8, 16]} /><meshStandardMaterial color={night ? "#2A2E3A" : "#CFCABB"} roughness={1} /></mesh>
      <mesh ref={w1} position={[0, 1.5, 0]}><cylinderGeometry args={[0.12, 0.2, 1.3, 10]} /><meshStandardMaterial color={"#A9DCEC"} transparent opacity={0.7} emissive={"#A9DCEC"} emissiveIntensity={night ? 0.4 : 0.1} /></mesh>
      <mesh ref={w2} position={[0, 1.15, 0]}><cylinderGeometry args={[0.45, 0.55, 0.7, 12]} /><meshStandardMaterial color={"#BFE6F2"} transparent opacity={0.55} /></mesh>
    </group>
  );
}

export default function Decor({ buildings }) {
  const night = useNight();
  const obstacles = useMemo(() => worldObstacles(buildings, 5), [buildings]);
  const fountainPos = useMemo(() => [9, 0, 9], []);
  const obs = useMemo(() => [...obstacles, { x: fountainPos[0], z: fountainPos[2], r: 3.5 }], [obstacles, fountainPos]);

  const trees = useMemo(() => scatterPositions({ count: 28, radius: 34, innerHole: 5, seed: 11, obstacles: obs, avoidDist: 5, minSpacing: 3.4 }), [obs]);
  const bushes = useMemo(() => scatterPositions({ count: 18, radius: 31, innerHole: 4, seed: 23, obstacles: obs, avoidDist: 4, minSpacing: 2.6 }), [obs]);
  const rocks = useMemo(() => scatterPositions({ count: 10, radius: 33, innerHole: 4, seed: 37, obstacles: obs, avoidDist: 4, minSpacing: 3 }), [obs]);
  const lamps = useMemo(() => scatterPositions({ count: 9, radius: 21, innerHole: 5, seed: 53, obstacles: obs, avoidDist: 4.5, minSpacing: 5 }), [obs]);
  const benches = useMemo(() => getBenches(buildings), [buildings]);
  const flowerbeds = useMemo(() => scatterPositions({ count: 6, radius: 19, innerHole: 5, seed: 83, obstacles: obs, avoidDist: 4.5, minSpacing: 4.5 }), [obs]);
  const meadow = useMemo(() => scatterPositions({ count: 44, radius: 33, innerHole: 4, seed: 131, obstacles: obs, avoidDist: 3.0, minSpacing: 1.9 }), [obs]);

  return (
    <group>
      <Fountain position={fountainPos} />

      {flowerbeds.map((p, i) => (<FlowerBed key={`f${i}`} position={[p.x, 0, p.z]} />))}
      {meadow.map((p, i) => (<FloraCluster key={`mf${i}`} position={[p.x, 0, p.z]} seed={(i + 1) * 17 + 5} night={night} count={4 + Math.floor(p.rnd * 4)} spread={0.5 + p.rnd * 0.5} scale={0.9 + p.rnd * 0.7} />))}
      {benches.map((p, i) => (<Bench key={`b${i}`} position={[p.x, 0, p.z]} rotation={p.a} />))}
      {lamps.map((p, i) => (<Lamp key={`l${i}`} position={[p.x, 0, p.z]} />))}
      {bushes.map((p, i) => (<Bush key={`bu${i}`} position={[p.x, 0, p.z]} scale={0.8 + p.rnd * 0.7} />))}
      {rocks.map((p, i) => (<Rock key={`r${i}`} position={[p.x, 0, p.z]} scale={0.7 + p.rnd * 0.8} />))}
      {trees.map((p, i) => (<Tree key={`t${i}`} position={[p.x, 0, p.z]} scale={0.8 + p.rnd * 0.8} />))}
    </group>
  );
}
